/** biome-ignore-all lint/nursery/useExplicitType: Zod schema inference */
import type { IProgressCardBucketFilter, IProgressCardStateFilter } from '@repo/config';
import { z } from 'zod';
import { createApiResponseSchema, type IAPI } from '../../apiResponse';

export const progressCardItemSchema = z.object({
  challengeId: z.number(),
  slug: z.string(),
  title: z.string(),
  type: z.string(),
  cefrLevel: z.string(),
  level: z.number(),
  nextReviewAt: z.number(),
  passed: z.number(),
  failed: z.number(),
});

export interface IProgressCardItem {
  challengeId: number;
  slug: string;
  title: string;
  type: string;
  cefrLevel: string;
  level: number;
  nextReviewAt: number;
  passed: number;
  failed: number;
}

const progressCardCountsSchema = z.object({
  total: z.number(),
  totalAll: z.number(),
  buckets: z.object({
    new: z.number(),
    learning: z.number(),
    strong: z.number(),
    mastered: z.number(),
  }),
  states: z.object({
    due: z.number(),
    struggling: z.number(),
    never: z.number(),
  }),
});

export interface IProgressCardCounts {
  total: number;
  totalAll: number;
  buckets: { new: number; learning: number; strong: number; mastered: number };
  states: { due: number; struggling: number; never: number };
}

const progressCardsDataSchema = z.object({
  items: z.array(progressCardItemSchema),
  nextCursor: z.string().nullable(),
  filters: z.object({
    bucket: z.string(),
    level: z.number().nullable(),
    state: z.string(),
    cefr: z.string().nullable(),
  }),
  access: z.object({
    premium: z.boolean(),
    capLimit: z.number(),
    capApplied: z.boolean(),
    hiddenCount: z.number(),
  }),
  counts: progressCardCountsSchema.nullable(),
});

export interface IProgressCardsData {
  items: IProgressCardItem[];
  nextCursor: string | null;
  filters: {
    bucket: IProgressCardBucketFilter;
    level: number | null;
    state: IProgressCardStateFilter;
    cefr: string | null;
  };
  access: {
    premium: boolean;
    capLimit: number;
    capApplied: boolean;
    hiddenCount: number;
  };
  counts: IProgressCardCounts | null;
}

export const getProgressCardsV1ResponseSchema = createApiResponseSchema(
  progressCardsDataSchema,
) as z.ZodSchema<IAPI<IProgressCardsData>>;

export type IGetProgressCardsV1Response = IAPI<IProgressCardsData>;
